"use client";

/**
 * Scene 5 — Decide.
 *
 * Commit moment of Landing v2. All five prior scenes converge into this
 * mega-hero "Find yours." + primary CTA + secondary ghost.
 *
 * Authoritative spec:
 *   - research/landing-v2/09-wireframe-v2-part-b.md §7
 *   - research/landing-v2/05-landing-concept-v2.md §4.6
 *
 * Voice: Mira calm-confident. "Find yours." reads as a statement of fact,
 * not an aggressive push.
 *
 * Motion beats (desktop, on whileInView once 0.3):
 *   t=0      SceneHeader dual-rail fade-in (via shared primitive)
 *   t=200ms  mega-hero gradient-clip reveal — word-stagger
 *            "Find"  @ 200ms · "yours."  @ 320ms, each 600ms easeOut
 *   t=800ms  subhead fade-up y:12→0 400ms
 *   t=1200ms primary CTA reveal: y:16→0 500ms
 *   t=1600ms secondary ghost fade-in 300ms
 *
 * Reduced-motion: all entries instant, no hover translate, no glow pulse.
 *
 * a11y:
 *   - SceneHeader h2 "05 · DECIDE" labels the section
 *   - Mega-hero rendered as visually-large <h2> (page H1 lives in Scene 0)
 *   - Primary CTA: <button> so Space key activates
 *   - Secondary ghost: <Link> anchor semantics
 *   - focus-visible rings amber-300 ring-2 ring-offset-2 ring-offset-zinc-950
 */

import { motion, useReducedMotion } from "framer-motion";
import { useCallback, useState } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { Sparkles } from "lucide-react";

import { SceneContainer } from "../shared/SceneContainer";
import { AmbientBackground } from "../shared/AmbientBackground";
import { useSceneView } from "../shared/useSceneView";
import { SceneHeader } from "@/components/shared-primitives/SceneHeader";
import { getTopPickShort } from "../data/demoNiches.short";
import { trackEvent } from "@/lib/analytics";

// ─── Copy (single source of truth — scan here for never-say regex) ─────────

const COPY = {
  megaHeroWords: ["Find", "yours."] as const,
  megaHeroPlain: "Find yours.",
  subheadLine1: "Everyone has a niche.",
  subheadLine2: "Ours is helping you find yours.",
  primaryCta: "Start — first niche free",
  secondaryCta: "See a real one",
} as const;

// ─── Routes & constants ────────────────────────────────────────────────────

const APP_ROUTE = "/app";

// ─── Motion timings ────────────────────────────────────────────────────────

const APPLE_EASE_OUT = [0.16, 1, 0.3, 1] as const;

const TIMING = {
  megaWord1: 0.2,
  megaWord2: 0.32,
  subhead: 0.8,
  primaryCta: 1.2,
  secondaryCta: 1.6,
} as const;

const DURATION = {
  mega: 0.6,
  subhead: 0.4,
  primary: 0.5,
  secondary: 0.3,
  reducedFallback: 0.2,
} as const;

// ─── Component ─────────────────────────────────────────────────────────────

export default function Scene5Decide() {
  const reduced = useReducedMotion();
  const router = useRouter();

  const topPick = getTopPickShort();
  const topPickSlug = topPick.id;
  const demoNicheHref = `/demo/niche/${topPickSlug}`;

  useSceneView({
    sceneId: "5",
    sceneName: "Decide",
    elementId: "scene-5-decide",
  });

  // Dedupe guard — rapid double-click on primary CTA can double-fire
  // router.push. Latch on first click; page navigates away so no reset needed.
  const [isNavigating, setIsNavigating] = useState(false);

  const handlePrimaryClick = useCallback(() => {
    if (isNavigating) return;
    trackEvent("landing.primary_cta.click", { cta_label: COPY.primaryCta });
    setIsNavigating(true);
    router.push(APP_ROUTE);
  }, [isNavigating, router]);

  const handleSecondaryClick = useCallback(() => {
    trackEvent("landing.secondary_cta.click", { cta_label: COPY.secondaryCta });
  }, []);

  return (
    <SceneContainer
      id="scene-5-decide"
      ariaLabel="Decide — start building"
    >
      <div className="relative grid min-h-[calc(100svh-var(--landing-mobile-nav-clearance,0px))] w-full place-items-center overflow-hidden px-6 pb-[calc(8.5rem+env(safe-area-inset-bottom))] pt-16 md:min-h-screen md:py-24">
        {/* Ambient gradient — center origin for commit moment */}
        <AmbientBackground origin="center" />

        {/* Content column — centered both axes */}
        <div className="relative z-10 flex w-full max-w-3xl flex-col items-center text-center">
          {/* Dual-rail scene header — "04 · DECIDE" (Scene 3 Giants removed). */}
          <SceneHeader
            id="scene-5-decide-title"
            number={4}
            title="Decide"
            className="mb-10 w-full md:mb-20"
          />

          {/* MEGA-HERO — "Find yours." gradient-clip word-stagger */}
          <h2
            aria-label={COPY.megaHeroPlain}
            className="font-heading font-bold tracking-tight leading-[1] text-[60px] sm:text-[72px] md:text-[88px] lg:text-[96px]"
          >
            {COPY.megaHeroWords.map((word, idx) => (
              <motion.span
                key={word}
                aria-hidden
                className="inline-block bg-gradient-to-r from-amber-300 via-amber-200 to-white bg-clip-text text-transparent"
                style={idx === 0 ? { marginRight: "0.25em" } : undefined}
                initial={reduced ? { opacity: 1, y: 0 } : { opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{
                  duration: reduced ? 0 : DURATION.mega,
                  delay: reduced
                    ? 0
                    : idx === 0
                      ? TIMING.megaWord1
                      : TIMING.megaWord2,
                  ease: APPLE_EASE_OUT,
                }}
              >
                {word}
              </motion.span>
            ))}
          </h2>

          {/* Subhead — Bricolage italic bold per Scene 0 pattern */}
          <motion.p
            initial={reduced ? { opacity: 1, y: 0 } : { opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{
              duration: reduced ? DURATION.reducedFallback : DURATION.subhead,
              delay: reduced ? 0 : TIMING.subhead,
              ease: APPLE_EASE_OUT,
            }}
            className="mt-6 max-w-md font-heading italic font-bold leading-[1.5] text-[18px] text-white/65 md:mt-8 md:text-[20px]"
          >
            {COPY.subheadLine1}
            <br />
            {COPY.subheadLine2}
          </motion.p>

          {/* PRIMARY CTA — "Start — first niche free →" */}
          <motion.div
            initial={reduced ? { opacity: 1, y: 0 } : { opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{
              duration: reduced ? DURATION.reducedFallback : DURATION.primary,
              delay: reduced ? 0 : TIMING.primaryCta,
              ease: APPLE_EASE_OUT,
            }}
            className="relative mt-8 flex w-full max-w-xs items-center justify-center md:mt-12 md:max-w-none md:w-auto"
          >
            <button
              type="button"
              onClick={handlePrimaryClick}
              disabled={isNavigating}
              aria-busy={isNavigating || undefined}
              className="group inline-flex h-14 w-full items-center justify-center gap-2 rounded-xl bg-amber-400 px-8 font-heading text-base font-bold text-zinc-900 shadow-[0_0_40px_-10px_rgba(251,191,36,0.5)] transition-all duration-200 ease-out hover:bg-amber-300 hover:shadow-[0_0_56px_-8px_rgba(251,191,36,0.6)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300 focus-visible:ring-offset-2 focus-visible:ring-offset-zinc-950 disabled:pointer-events-none disabled:opacity-60 md:h-14 md:w-auto md:text-lg"
            >
              <span>{COPY.primaryCta}</span>
              <span
                aria-hidden
                className={
                  reduced
                    ? "inline-block"
                    : "inline-block transition-transform duration-200 ease-out group-hover:translate-x-1"
                }
              >
                →
              </span>
            </button>
          </motion.div>

          {/* SECONDARY GHOST — "[sparkle] See a real one" → top-pick demo */}
          <motion.div
            initial={reduced ? { opacity: 1 } : { opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{
              duration: reduced ? DURATION.reducedFallback : DURATION.secondary,
              delay: reduced ? 0 : TIMING.secondaryCta,
              ease: APPLE_EASE_OUT,
            }}
            className="mt-5"
          >
            <Link
              href={demoNicheHref}
              prefetch
              onClick={handleSecondaryClick}
              className="inline-flex h-11 items-center gap-2 rounded-lg border border-white/20 bg-transparent px-5 font-heading text-[15px] font-medium text-white/80 transition-colors duration-200 ease-out hover:border-white/30 hover:bg-white/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300 focus-visible:ring-offset-2 focus-visible:ring-offset-zinc-950"
            >
              <Sparkles
                size={16}
                strokeWidth={2}
                className="text-amber-300"
                aria-hidden
              />
              {COPY.secondaryCta}
            </Link>
          </motion.div>

        </div>
      </div>
    </SceneContainer>
  );
}
